import { createContext, useContext, useState, useEffect, type ReactNode } from 'react';
import type { Locale } from './config';
import { locales, defaultLocale, shortToFullLocale } from './config';
import { systemApi } from '@/lib/api/system';
import { useQuery } from '@tanstack/react-query';

interface LocaleContextType {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  supportedLocales: Locale[];
  isLoading: boolean;
}

const LocaleContext = createContext<LocaleContextType | undefined>(undefined);

function getCookieLocale(): Locale | null {
  if (typeof document === 'undefined') return null;
  const cookies = document.cookie.split(';');
  for (const cookie of cookies) {
    const [name, value] = cookie.trim().split('=');
    if (name === 'locale' && locales.includes(value as Locale)) {
      return value as Locale;
    }
  }
  return null;
}

export function LocaleProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(defaultLocale);

  // Use TanStack Query for i18n config - this will auto-refetch when cache is invalidated
  const { data: config, isLoading } = useQuery({
    queryKey: ['system', 'i18n-config'],
    queryFn: systemApi.getI18nConfig,
    staleTime: Infinity, // Config doesn't go stale, we control invalidation
  });

  // Derive supported locales from config
  const supportedLocales: Locale[] = config
    ? (config.supportedLocales
        .map((short: string) => shortToFullLocale[short])
        .filter((loc): loc is Locale => loc !== undefined))
    : [...locales];

  // Get default locale from config
  const apiDefaultLocale: Locale = config
    ? shortToFullLocale[config.defaultLocale] || defaultLocale
    : defaultLocale;

  // Set locale based on cookie or API default after config loads
  useEffect(() => {
    if (isLoading) return;

    const cookieLocale = getCookieLocale();
    if (cookieLocale && supportedLocales.includes(cookieLocale)) {
      setLocaleState(cookieLocale);
    } else {
      setLocaleState(apiDefaultLocale);
    }
  }, [isLoading, apiDefaultLocale, supportedLocales]);

  const setLocale = (newLocale: Locale) => {
    if (supportedLocales.includes(newLocale)) {
      setLocaleState(newLocale);
      document.cookie = `locale=${newLocale}; path=/; max-age=31536000; SameSite=lax`;
    }
  };

  return (
    <LocaleContext.Provider value={{ locale, setLocale, supportedLocales, isLoading }}>
      {children}
    </LocaleContext.Provider>
  );
}

export function useLocale() {
  const context = useContext(LocaleContext);
  if (!context) {
    throw new Error('useLocale must be used within LocaleProvider');
  }
  return context;
}
